import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NHLogo from '../components/NHLogo';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaToken, setMfaToken] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [cognitoUser, setCognitoUser] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [ssoProviders, setSsoProviders] = useState([]);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user was auto-logged out due to inactivity
  useEffect(() => {
    if (window.__sessionExpired) {
      setSessionExpired(true);
      delete window.__sessionExpired;
    }
  }, []);

  // Load available SSO providers
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoError = params.get('error');
    if (ssoError) setError(ssoError);

    fetch('/api/auth/saml/available')
      .then(r => r.json())
      .then(r => { if (r.success && r.data?.length) setSsoProviders(r.data); })
      .catch(() => {}); // Graceful — SSO is optional
  }, []);

  // Get the redirect path from location state, or default to dashboard
  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // MFA verification step
    if (mfaToken) {
      try {
        const api = (await import('../services/api')).default;
        const result = await api.request('/auth/mfa/verify', {
          method: 'POST',
          body: JSON.stringify({ mfaToken, code: mfaCode }),
        });
        if (result.success) {
          if (result.data?.needsContextSelection) {
            navigate('/select-context', { replace: true });
          } else if (result.data?.token) {
            localStorage.setItem('token', result.data.token);
            window.location.href = from;
          }
        } else {
          setError(result.error || 'Invalid MFA code');
        }
      } catch (err) {
        setError(err.message || 'MFA verification failed');
      }
      setIsLoading(false);
      return;
    }

    // New password completion step
    if (cognitoUser) {
      if (!newPassword || !confirmNewPassword) {
        setError('Please enter and confirm your new password');
        setIsLoading(false);
        return;
      }
      if (newPassword !== confirmNewPassword) {
        setError('Passwords do not match');
        setIsLoading(false);
        return;
      }
      if (newPassword.length < 8) {
        setError('Password must be at least 8 characters');
        setIsLoading(false);
        return;
      }
      try {
        const cognitoAuthService = (await import('../services/cognitoAuthService')).default;
        const tokens = await cognitoAuthService.completeNewPassword(cognitoUser, newPassword);
        // Now log in with the new token
        const api = (await import('../services/api')).default;
        const data = await api.request('/auth/cognito-login', {
          method: 'POST',
          body: JSON.stringify({ idToken: tokens.idToken }),
        });
        if (data.success) {
          const authService = (await import('../services/authService')).default;
          authService.setToken(data.data.token);
          authService.setUser(data.data.user);
          if (data.data.needsContextSelection) {
            navigate('/select-context', { replace: true });
          } else {
            navigate(from, { replace: true });
          }
        } else {
          setError(data.error || 'Login failed after password change');
        }
      } catch (err) {
        setError(err.message || 'Failed to set new password');
      }
      setIsLoading(false);
      return;
    }

    try {
      const result = await login(email, password);

      if (result.success) {
        if (result.mfaRequired) {
          setMfaToken(result.mfaToken);
          setIsLoading(false);
          return;
        }
        if (result.needsContextSelection) {
          navigate('/select-context', { replace: true });
        } else {
          navigate(from, { replace: true });
        }
      } else if (result.newPasswordRequired) {
        setCognitoUser(result.cognitoUser);
        setError('');
      } else if (result.passwordResetRequired) {
        // GH-11: super-admin reset the user's password — Cognito needs the
        // forgot-password flow to complete before they can log in. Hand off
        // to /forgot-password with the email pre-filled so the user lands
        // on the reset code form instead of seeing "Invalid email or password".
        navigate('/forgot-password', { state: { email: result.email || email, forcedReset: true }, replace: true });
      } else {
        // Never show Cognito-specific errors on non-Cognito builds
        const errMsg = result.error || 'Login failed';
        setError(errMsg.includes('Cognito') ? 'Invalid email or password' : errMsg);
      }
    } catch (err) {
      const errMsg = err.message || 'An error occurred during login';
      setError(errMsg.includes('Cognito') ? 'Invalid email or password' : errMsg);
    }

    setIsLoading(false);
  };

  return (
    <div style={styles.container}>
      <div style={styles.loginCard}>
        <div style={styles.header}>
          <NHLogo size={40} style={{ justifyContent: 'center' }} />
          <p style={styles.subtitle}>Outbound Engagement Platform</p>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <h2 style={styles.formTitle}>{cognitoUser ? 'Set New Password' : mfaToken ? 'Two-Factor Authentication' : 'Sign In'}</h2>

          {sessionExpired && !mfaToken && (
            <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#92400e' }}>
              Your session expired due to inactivity. Please sign in again.
            </div>
          )}

          {error && (
            <div style={styles.error}>
              {error}
            </div>
          )}

          {cognitoUser ? (
            <>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                Your account requires a new password. Please set one below.
              </p>
              <div style={styles.inputGroup}>
                <label style={styles.label} htmlFor="newPassword">New Password</label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  style={styles.input}
                  disabled={isLoading}
                  autoFocus
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label} htmlFor="confirmNewPassword">Confirm Password</label>
                <input
                  id="confirmNewPassword"
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  style={styles.input}
                  disabled={isLoading}
                />
              </div>
              <button
                type="submit"
                style={{ ...styles.button, ...(isLoading ? styles.buttonDisabled : {}) }}
                disabled={isLoading}
              >
                {isLoading ? 'Setting password...' : 'Set Password & Sign In'}
              </button>
              <button type="button" onClick={() => { setCognitoUser(null); setNewPassword(''); setConfirmNewPassword(''); setError(''); }}
                style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'center' }}>
                Back to sign in
              </button>
            </>
          ) : mfaToken ? (
            <div style={styles.inputGroup}>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
                Enter the 6-digit code from your authenticator app, or a recovery code.
              </p>
              <label style={styles.label} htmlFor="mfaCode">Authentication Code</label>
              <input
                id="mfaCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="000000"
                required
                style={{ ...styles.input, textAlign: 'center', fontSize: '20px', letterSpacing: '6px' }}
                disabled={isLoading}
                autoFocus
              />
              <button type="button" onClick={() => { setMfaToken(null); setMfaCode(''); setError(''); }}
                style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}>
                Back to sign in
              </button>
            </div>
          ) : (
            <>
              <div style={styles.inputGroup}>
                <label style={styles.label} htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  style={styles.input}
                  disabled={isLoading}
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label} htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
              required
              style={styles.input}
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            style={{
              ...styles.button,
              ...(isLoading ? styles.buttonDisabled : {})
            }}
            disabled={isLoading}
          >
            {isLoading ? (mfaToken ? 'Verifying...' : 'Signing in...') : (mfaToken ? 'Verify Code' : 'Sign In')}
          </button>

          {!mfaToken && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <Link to="/forgot-password" style={{ fontSize: 13, color: '#6D28D9', textDecoration: 'none' }}>
                Forgot Password?
              </Link>
            </div>
          )}
          </>
          )}
        </form>

        {ssoProviders.length > 0 && (
          <div style={{ marginTop: 20, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
            <p style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', marginBottom: 12 }}>Or sign in with your EHR</p>
            {ssoProviders.map(p => (
              <button key={p.practiceId}
                onClick={() => { window.location.href = `/api/auth/saml/login?practiceId=${p.practiceId}`; }}
                style={{
                  width: '100%', padding: '10px 16px', marginBottom: 8,
                  background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
                  fontSize: 14, fontWeight: 500, color: '#334155', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition: 'all 0.15s',
                }}
                onMouseOver={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                onMouseOut={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
              >
                <span style={{ fontSize: 16 }}>🏥</span>
                Sign in with {p.practiceName}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px',
  },
  loginCard: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
    width: '100%',
    maxWidth: '400px',
    overflow: 'hidden',
  },
  header: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '30px',
    textAlign: 'center',
    color: '#fff',
  },
  title: {
    margin: 0,
    fontSize: '28px',
    fontWeight: '700',
  },
  subtitle: {
    margin: '8px 0 0',
    fontSize: '14px',
    opacity: 0.9,
  },
  form: {
    padding: '30px',
  },
  formTitle: {
    margin: '0 0 24px',
    fontSize: '20px',
    color: '#333',
    textAlign: 'center',
  },
  error: {
    background: '#fee',
    color: '#c00',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '20px',
    fontSize: '14px',
    textAlign: 'center',
  },
  inputGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#555',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '16px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  button: {
    width: '100%',
    padding: '14px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#fff',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'opacity 0.2s, transform 0.2s',
  },
  buttonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
};

export default Login;
